if (!customElements.get('sl-scroll-intro')) {
 customElements.define('sl-scroll-intro', class extends HTMLElement {
  connectedCallback() {
   if (this.initialized) return;
   this.initialized = true;
   this.config = JSON.parse(this.querySelector('[data-sl-sequences]').textContent);
   this.model = this.dataset.model === 'signals' ? 'signals' : 'beam';
   this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
   this.mobile = matchMedia('(max-width: 749px)');
   this.static = this.reduced.matches || navigator.connection?.saveData === true;
   this.video = this.querySelector('video');
   this.pin = this.querySelector('.sl-si-pin');
   this.poster = this.querySelector('.sl-si-poster');
   this.panels = [...this.querySelectorAll('.sl-si-panel')];
   this.motion = this.querySelector('.sl-si-motion');
   this.buttons = [...this.querySelectorAll('[data-model]')];
   this.step = this.querySelector('[data-step]');
   this.dataset.enhanced = '';
   this.video.muted = true;
   this.video.defaultMuted = true;
   this.schedule = () => {
    if (!this.raf && !document.hidden && this.isConnected) {
     this.raf = requestAnimationFrame(now => { this.raf = null; this.draw(now); });
    }
   };
   this.resize = () => {
    if ((this.mobile.matches ? 'mobile' : 'desktop') !== this.format) this.select();
    this.schedule();
   };
   this.onReduced = () => { this.static = this.reduced.matches; this.setMode(); };
   this.onMotion = () => { this.static = !this.static; this.setMode(true); };
   this.onModel = event => {
    const next = event.currentTarget.dataset.model;
    if (next !== this.model) { this.model = next; this.select(); }
   };
   this.onReady = () => {
    if ((this.progress || 0) < .003 && this.video.readyState >= 2) this.hasFrame = true;
    this.schedule();
   };
   this.onSeeked = () => { this.hasFrame = true; this.schedule(); };
   this.onError = () => {
    if (this.video.getAttribute('src')) { this.static = true; this.setMode(true); }
   };
   this.onVisibility = () => {
    this.lastTick = null;
    if (document.hidden) { cancelAnimationFrame(this.raf); this.raf = null; }
    else { if (this.prewarm) this.load(); this.schedule(); }
   };
   this.buttons.forEach(button => button.addEventListener('click', this.onModel));
   this.motion.addEventListener('click', this.onMotion);
   this.reduced.addEventListener('change', this.onReduced);
   this.video.addEventListener('loadedmetadata', this.onReady);
   this.video.addEventListener('loadeddata', this.onReady);
   this.video.addEventListener('canplay', this.onReady);
   this.video.addEventListener('seeked', this.onSeeked);
   this.video.addEventListener('error', this.onError);
   window.addEventListener('resize', this.resize, {passive:true});
   window.addEventListener('scroll', this.schedule, {passive:true});
   document.addEventListener('visibilitychange', this.onVisibility);
   this.near = false;
   this.prewarm = false;
   this.observer = new IntersectionObserver(entries => {
    if (entries.some(e => e.isIntersecting)) {
     this.near = true;
     if (this.prewarm || this.getBoundingClientRect().top < innerHeight) this.load();
     this.schedule();
    }
   }, {rootMargin:'1200px'});
   this.observer.observe(this);
   this.setMode();
   // Give the opening film its first frame before preloading the selected sequence.
   this.hero = document.querySelector('.sl-studio-hero video');
   this.warmup = () => {
    if (!this.isConnected || this.prewarm) return;
    this.prewarm = true;
    clearTimeout(this.warmTimer);
    this.hero?.removeEventListener('loadeddata', this.warmup);
    this.load();
   };
   if (this.hero && this.hero.readyState < 2) this.hero.addEventListener('loadeddata', this.warmup, {once:true});
   this.warmTimer = setTimeout(this.warmup, this.hero?.readyState >= 2 ? 250 : 1800);
  }
  setMode(keepPosition = false) {
   const top = this.getBoundingClientRect().top;
   this.toggleAttribute('data-static', this.static);
   this.motion.textContent = this.static ? this.motion.dataset.resume : this.motion.dataset.pause;
   this.motion.setAttribute('aria-pressed', String(this.static));
   if (keepPosition && top < 0) window.scrollTo({top:window.scrollY + top, behavior:'instant'});
   this.select();
  }
  select() {
   this.format = this.mobile.matches ? 'mobile' : 'desktop';
   this.hasFrame = false;
   this.followProgress = null;
   this.lastTick = null;
   this.lastSeek = -Infinity;
   this.video.pause();
   this.video.removeAttribute('src');
   this.video.load();
   this.video.style.opacity = '0';
   this.poster.style.opacity = '1';
   const key = this.static ? 'still' : 'poster';
   this.poster.querySelector('source').srcset = this.config[this.model].mobile[key];
   this.poster.querySelector('img').src = this.config[this.model].desktop[key];
   this.buttons.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.model === this.model)));
   this.querySelectorAll('[data-model-heading],[data-model-description]').forEach(el => { el.textContent = el.dataset[this.model]; });
   if (this.near || this.prewarm) this.load();
   this.schedule();
  }
  load() {
   if (this.static || document.hidden || this.video.getAttribute('src')) return;
   this.video.preload = 'auto';
   this.video.src = this.config[this.model][this.format].video;
   this.video.load();
  }
  draw(now) {
   if (!this.isConnected) return;
   const rect = this.getBoundingClientRect();
   const span = Math.max(1, this.offsetHeight - this.pin.offsetHeight);
   this.progress = this.static ? 0 : Math.max(0, Math.min(1, -rect.top / span));
   const active = rect.top < innerHeight && rect.bottom > 0;
   if (active && this.near) this.load();
   const dt = this.lastTick == null ? 16 : Math.min(64, now - this.lastTick);
   this.lastTick = now;
   if (this.followProgress == null || !active || this.static) this.followProgress = this.progress;
   else this.followProgress += (this.progress - this.followProgress) * (1 - Math.exp(-dt / 85));
   const settling = Math.abs(this.progress - this.followProgress) > .0005;
   if (!settling) this.followProgress = this.progress;
   this.style.setProperty('--si-progress', this.followProgress.toFixed(4));
   const duration = this.video.duration;
   let pending = false;
   if (!this.static && Number.isFinite(duration) && duration > 0 && this.video.readyState >= 2) {
    this.targetTime = Math.min(duration - 1/30, Math.round(this.followProgress * (duration * 30 - 1)) / 30);
    pending = Math.abs(this.video.currentTime - this.targetTime) > .017;
    // Coalesce scroll events and avoid seeking faster than the 30 fps source.
    if (pending && !this.video.seeking && now - this.lastSeek >= 32) {
     this.lastSeek = now;
     this.video.currentTime = this.targetTime;
    }
   }
   const visible = !this.static && this.hasFrame && this.followProgress > .003;
   this.video.style.opacity = visible ? '1' : '0';
   this.poster.style.opacity = visible ? '0' : '1';
   const chapter = this.static ? 3 : this.followProgress < .22 ? 0 : this.followProgress < .50 ? 1 : this.followProgress < .75 ? 2 : 3;
   this.panels.forEach((panel,i) => { panel.classList.toggle('is-active',i===chapter); panel.setAttribute('aria-hidden',String(i!==chapter)); });
   this.step.textContent = '0' + (chapter+1) + ' / 04';
   if (active && !this.static && (settling || (pending && !this.video.seeking))) this.schedule();
  }
  disconnectedCallback() {
   this.observer?.disconnect();
   clearTimeout(this.warmTimer);
   this.hero?.removeEventListener('loadeddata', this.warmup);
   window.removeEventListener('scroll', this.schedule);
   window.removeEventListener('resize', this.resize);
   document.removeEventListener('visibilitychange', this.onVisibility);
   this.reduced?.removeEventListener('change', this.onReduced);
   this.motion?.removeEventListener('click', this.onMotion);
   this.buttons?.forEach(b => b.removeEventListener('click', this.onModel));
   for (const event of ['loadedmetadata','loadeddata','canplay']) this.video?.removeEventListener(event,this.onReady);
   this.video?.removeEventListener('seeked',this.onSeeked);
   this.video?.removeEventListener('error',this.onError);
   this.video?.pause();
   cancelAnimationFrame(this.raf);
   this.raf = null;
   this.initialized = false;
  }
 });
}
